import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from '../api';
import { disableGoogleAutoSelect } from './googleIdentity';

export type SubscriptionAssignmentState =
  | 'active'
  | 'scheduled'
  | 'expired'
  | 'inactive'
  | 'plan_inactive';

export type SubscriptionAssignment = {
  id: string;
  subscriptionId: string;
  subscriptionCode: string;
  subscriptionName: string;
  subscriptionDescription: string | null;
  subscriptionStatus: 'active' | 'inactive';
  status: SubscriptionAssignmentState;
  isManuallyActive: boolean;
  startAt: string | null;
  endAt: string | null;
  addedAt: string;
  addedByUserId: string | null;
  addedReason: string | null;
  addedVia: string;
  updatedAt: string;
};

export type SubscriptionStatus = {
  allowed: boolean;
  reason: 'none' | 'missing' | 'expired' | 'inactive' | 'plan_inactive';
  summary: string;
  activeAssignmentId: string | null;
  nextExpiration: string | null;
  contactEmail: string | null;
  assignments: SubscriptionAssignment[];
};

export type User = {
  id: string;
  name: string | null;
  studentName: string | null;
  email: string;
  verified_at: string | null;
  created_at: string;
  role: 'standard' | 'admin';
  isActive: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  signUpAt: string;
  signUpIp: string | null;
  subscriptionStatus: SubscriptionStatus;
};

type MeResponse = {
  user: User;
};

export type AuthContextValue = {
  authToken: string | null;
  user: User | null;
  authChecked: boolean;
  reauthReason: string | null;
  setReauthReason: (value: string | null) => void;
  loginSuccess: (token: string, user: User) => void;
  authedRequest: <T = any>(path: string, opts?: RequestInit) => Promise<T>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredToken(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('authToken');
  } catch {
    return null;
  }
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

export function LoadingScreen({ message }: { message: string }) {
  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Selective Tests</h1>
        <p className="auth-subtitle">{message}</p>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialTokenRef = useRef<string | null>(readStoredToken());
  const [authToken, setAuthToken] = useState<string | null>(initialTokenRef.current);
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState<boolean>(() => !initialTokenRef.current);
  const [reauthReason, setReauthReason] = useState<string | null>(null);
  const lastVerifiedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      if (!authToken) {
        window.localStorage.removeItem('authToken');
      } else {
        window.localStorage.setItem('authToken', authToken);
      }
    } catch {
      // ignore storage errors
    }
  }, [authToken]);

  useEffect(() => {
    if (!authToken) {
      lastVerifiedTokenRef.current = null;
      setUser(null);
      setAuthChecked(true);
      return;
    }

    if (lastVerifiedTokenRef.current === authToken && user) {
      setAuthChecked(true);
      return;
    }

    let cancelled = false;
    setAuthChecked(false);

    (async () => {
      try {
        const data = await api<MeResponse>('/api/auth/me', {}, authToken);
        if (cancelled) return;
        setUser(data.user);
        lastVerifiedTokenRef.current = authToken;
        setAuthChecked(true);
      } catch (err: any) {
        if (cancelled) return;
        lastVerifiedTokenRef.current = null;
        setUser(null);
        setAuthToken(null);
        setAuthChecked(true);
        setReauthReason(err?.message || 'Session expired. Please sign in again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken, user]);

  const authedRequest = useCallback(
    async <T,>(path: string, opts: RequestInit = {}) => {
      if (!authToken) {
        throw new Error('Not authenticated');
      }
      try {
        return await api<T>(path, opts, authToken);
      } catch (err: any) {
        const status: number | undefined = err?.status;
        const message: string = err?.message || 'Session expired. Please sign in again.';
        if (status === 401 || (status === 403 && message.toLowerCase().includes('inactive'))) {
          lastVerifiedTokenRef.current = null;
          setAuthToken(null);
          setUser(null);
          setAuthChecked(true);
          setReauthReason(message);
        }
        throw err;
      }
    },
    [authToken]
  );

  const logout = useCallback(() => {
    disableGoogleAutoSelect();
    lastVerifiedTokenRef.current = null;
    setAuthToken(null);
    setUser(null);
    setReauthReason(null);
    setAuthChecked(true);
  }, []);

  const loginSuccess = useCallback((token: string, nextUser: User) => {
    lastVerifiedTokenRef.current = token;
    setAuthToken(token);
    setUser(nextUser);
    setReauthReason(null);
    setAuthChecked(true);
  }, []);

  const value = useMemo(
    () => ({
      authToken,
      user,
      authChecked,
      reauthReason,
      setReauthReason,
      loginSuccess,
      authedRequest,
      logout,
    }),
    [authToken, user, authChecked, reauthReason, loginSuccess, authedRequest, logout]
  );

  if (authToken && !authChecked) {
    return <LoadingScreen message="Checking your session…" />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
